import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatNumber, formatPercentage, calculateSummaryMetrics } from '../../utils/chartDataUtils';
import {
  prepareEngagementTrendData,
  prepareGrowthData,
  prepareContentPerformanceData,
  prepareQualityScoreData,
  prepareAudienceMetricsData,
  prepareAudienceMetricsTrendsData,
} from '../../utils/chartDataUtils';
import EngagementTrendChart from '../charts/EngagementTrendChart';
import GrowthChart from '../charts/GrowthChart';
import ContentPerformanceChart from '../charts/ContentPerformanceChart';
import QualityScoreChart from '../charts/QualityScoreChart';
import AudienceMetricsTrendsChart from '../charts/AudienceMetricsTrendsChart';
import MetricCard from '../MetricCard';
import ModernLoader from '../ModernLoader';
import styles from '../../styles/Team.module.css';

const Dashboard = ({ data, isLoading, error, onRetry }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'home' },
    { id: 'engagement', label: 'Engagement', icon: 'heart' },
    { id: 'growth', label: 'Growth', icon: 'users' },
    { id: 'content', label: 'Content', icon: 'file-alt' },
    { id: 'audience', label: 'Audience', icon: 'user-friends' }
  ];

  if (isLoading) {
    return <ModernLoader message="Analyzing your data" size="default" />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FontAwesomeIcon icon="exclamation-triangle" className={styles.errorIcon} />
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        {onRetry && (
          <button className={styles.retryBtn} onClick={onRetry}>
            <FontAwesomeIcon icon="sync" />
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <h3>No Data Available</h3>
        <p>Select a page and time period to view analytics data.</p>
      </div>
    );
  }

  const { period, metrics, analysis, recommendations, topPosts } = data;
  const { engagement, growth, reach } = metrics;
  
  // Calculate summary metrics from available data
  const summary = calculateSummaryMetrics(data);

  // Prepare chart data
  const engagementTrendData = prepareEngagementTrendData(data);
  const growthData = prepareGrowthData(data);
  const contentPerformanceData = prepareContentPerformanceData(data);
  const qualityScoreData = prepareQualityScoreData(data);
  const audienceMetricsData = prepareAudienceMetricsData(data);
  const audienceMetricsTrendsData = prepareAudienceMetricsTrendsData(data);

  // Content-specific data
  const { contentPerformance } = analysis || {};
  const avgQualityScore = contentPerformance?.qualityAnalysis?.avgQualityScore || 0;
  const highQualityPosts = contentPerformance?.qualityAnalysis?.highQualityPosts || 0;
  const mediumQualityPosts = contentPerformance?.qualityAnalysis?.mediumQualityPosts || 0;
  const lowQualityPosts = contentPerformance?.qualityAnalysis?.lowQualityPosts || 0;

  // Audience-specific data
  const { audienceMetrics } = analysis || {};
  const audienceQualityScore = audienceMetrics?.qualityMetrics?.audienceQualityScore || 0;
  const churnRate = audienceMetrics?.growthMetrics?.churnRate || 0;
  const retentionRate = audienceMetrics?.growthMetrics?.retentionRate || 0;
  const activityRate = audienceMetrics?.activityMetrics?.audienceActivityRate || 0;

  const renderOverviewTab = () => (
    <>
      {/* Key Metrics Grid */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title={`Total Posts (${period.days === 1 ? 'yesterday' : `last ${period.days} days`})`}
          value={summary.totalPosts ?? '—'}
          icon={<FontAwesomeIcon icon="chart-bar" />}
          color="blue"
        />
        <MetricCard
          title="Total Reach"
          value={summary.totalReach ?? '—'}
          icon={<FontAwesomeIcon icon="eye" />}
          color="green"
        />
        <MetricCard
          title="Total Engagement"
          value={summary.totalEngagements ?? '—'}
          icon={<FontAwesomeIcon icon="heart" />}
          color="purple"
        />
        <MetricCard
          title="Engagement Rate"
          value={summary.engagementRate ?? '—'}
          format="percentage"
          icon={<FontAwesomeIcon icon="chart-pie" />}
          color="orange"
        />
        <MetricCard
          title="Net Growth"
          value={growth.netGrowth ?? '—'}
          icon={<FontAwesomeIcon icon="user-plus" />}
          color="red"
        />
        <MetricCard
          title="Avg Daily Reach"
          value={reach.avgDailyReach ?? '—'}
          icon={<FontAwesomeIcon icon="arrow-up" />}
          color="blue"
        />
      </div>

      {/* Charts Grid */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Engagement Trends</h3>
          </div>
          <EngagementTrendChart data={engagementTrendData} />
        </div>
        
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Growth Analysis</h3>
          </div>
          <GrowthChart data={growthData} />
        </div>
        
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Content Performance</h3>
          </div>
          <ContentPerformanceChart data={contentPerformanceData} />
        </div>
        
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Quality Distribution</h3>
          </div>
          <QualityScoreChart data={qualityScoreData} />
        </div>
      </div>

      {/* Analytics Grid */}
      <div className={styles.analyticsGrid}>
        {/* Engagement Metrics */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Engagement Metrics</h3>
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.metricsList}>
              <div className={styles.metricItem}>
                <span>Total Engagement</span>
                <span>{formatNumber(engagement.totalEngagement)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Engagement Rate</span>
                <span>{formatPercentage(engagement.engagementRate)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Avg Engagement per Post</span>
                <span>{formatNumber(engagement.avgEngagementPerPost)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Total Comments</span>
                <span>{formatNumber(engagement.totalComments)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Growth Metrics */}
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Growth Metrics</h3>
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.metricsList}>
              <div className={styles.metricItem}>
                <span>Total New Followers</span>
                <span>{formatNumber(growth.totalNewFollowers)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Net Growth</span>
                <span>{formatNumber(growth.netGrowth)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Retention Rate</span>
                <span>{formatPercentage(growth.retentionRate)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Growth Trend</span>
                <span>{growth.growthTrend}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Posts */}
      {topPosts && topPosts.length > 0 && (
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Top Performing Posts</h3>
          </div>
          <div className={styles.topPostsList}>
            {topPosts.slice(0, 5).map((post, index) => (
              <div key={index} className={styles.topPostItem}>
                <div className={styles.postRank}>#{index + 1}</div>
                <div className={styles.postDetails}>
                  <div className={styles.postMessage}>
                    {post.message ? `${post.message.substring(0, 100)}...` : 'No message'}
                  </div>
                  <div className={styles.postStats}>
                    <span><FontAwesomeIcon icon="heart" /> {formatNumber(post.reactions_count || 0)}</span>
                    <span><FontAwesomeIcon icon="comment" /> {formatNumber(post.comments_count || 0)}</span>
                    <span><FontAwesomeIcon icon="share" /> {formatNumber(post.shares_count || 0)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Recommendations</h3>
          </div>
          <div className={styles.recommendationsList}>
            {recommendations.slice(0, 5).map((rec, index) => (
              <div key={index} className={styles.recommendationItem}>
                <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
                <span>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderEngagementTab = () => (
    <>
      {/* Engagement Metrics Grid */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Total Engagement"
          value={engagement.totalEngagement}
          icon={<FontAwesomeIcon icon="heart" />}
          color="purple"
        />
        <MetricCard
          title="Engagement Rate"
          value={engagement.engagementRate}
          format="percentage"
          icon={<FontAwesomeIcon icon="chart-pie" />}
          color="orange"
        />
        <MetricCard
          title="Avg Engagement per Post"
          value={engagement.avgEngagementPerPost}
          icon={<FontAwesomeIcon icon="chart-bar" />}
          color="blue"
        />
        <MetricCard
          title="Total Comments"
          value={engagement.totalComments}
          icon={<FontAwesomeIcon icon="comment" />}
          color="green"
        />
      </div>

      {/* Engagement Charts */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard} style={{ gridColumn: '1 / -1', minHeight: '420px' }}>
          <div className={styles.chartHeader}>
            <h3>Engagement Trends Over Time</h3>
          </div>
          <EngagementTrendChart data={engagementTrendData} />
        </div>
      </div>

      {/* Engagement Insights */}
      <div className={styles.analyticsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Engagement Recommendations</h3>
          </div>
          <div className={styles.recommendationsList}>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Focus on creating interactive content to boost engagement rates</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Ask questions in your posts to encourage comments</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Post consistently to maintain audience engagement</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderGrowthTab = () => (
    <>
      {/* Growth Metrics Grid */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Total New Followers"
          value={growth.totalNewFollowers ?? '—'}
          icon={<FontAwesomeIcon icon="user-plus" />}
          color="green"
        />
        <MetricCard
          title="Total Unfollows"
          value={growth.totalUnfollows !== undefined ? Math.abs(growth.totalUnfollows) : '—'}
          icon={<FontAwesomeIcon icon="user-minus" />}
          color="red"
        />
        <MetricCard
          title="Net Growth"
          value={growth.netGrowth ?? '—'}
          icon={<FontAwesomeIcon icon="chart-line" />}
          color="blue"
        />
        <MetricCard
          title="Retention Rate"
          value={growth.retentionRate ?? '—'}
          format="percentage"
          icon={<FontAwesomeIcon icon="shield-alt" />}
          color="purple"
        />
      </div>

      {/* Growth Charts */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Follower Growth Trends</h3>
          </div>
          <GrowthChart data={growthData} />
        </div>
        
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Growth Analysis</h3>
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.metricsList}>
              <div className={styles.metricItem}>
                <span>Growth Trend</span>
                <span style={{ 
                  color: growth.growthTrend === 'increasing' ? '#10b981' : 
                         growth.growthTrend === 'decreasing' ? '#ef4444' : '#f59e0b'
                }}>
                  {growth.growthTrend}
                </span>
              </div>
              <div className={styles.metricItem}>
                <span>Growth Consistency</span>
                <span>{formatPercentage(growth.growthConsistency)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Growth Acceleration</span>
                <span>{formatPercentage(growth.growthAcceleration)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Avg Daily Growth</span>
                <span>{formatNumber(growth.avgDailyGrowth)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Insights */}
      <div className={styles.analyticsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Growth Recommendations</h3>
          </div>
          <div className={styles.recommendationsList}>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Focus on content quality to improve retention rates</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Engage with your audience to build stronger connections</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Use targeted hashtags to reach new audiences</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderContentTab = () => (
    <>
      {/* Content Metrics Grid */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title={`Total Posts (${period.days === 1 ? 'yesterday' : `last ${period.days} days`})`}
          value={engagement.totalPosts}
          icon={<FontAwesomeIcon icon="file-alt" />}
          color="blue"
        />
        <MetricCard
          title="Avg Quality Score"
          value={avgQualityScore}
          icon={<FontAwesomeIcon icon="star" />}
          color="yellow"
        />
        <MetricCard
          title="High Quality Posts"
          value={highQualityPosts}
          icon={<FontAwesomeIcon icon="crown" />}
          color="green"
        />
        <MetricCard
          title="Content Types"
          value={Object.keys(contentPerformance?.contentTypes || {}).length}
          icon={<FontAwesomeIcon icon="tags" />}
          color="purple"
        />
      </div>

      {/* Content Charts */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Content Type Performance</h3>
          </div>
          <ContentPerformanceChart data={contentPerformanceData} />
        </div>
        
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Content Quality Distribution</h3>
          </div>
          <QualityScoreChart data={qualityScoreData} />
        </div>
      </div>

      {/* Content Insights */}
      <div className={styles.analyticsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Content Quality Breakdown</h3>
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.metricsList}>
              <div className={styles.metricItem}>
                <span>High Quality Posts</span>
                <span>{formatNumber(highQualityPosts)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Medium Quality Posts</span>
                <span>{formatNumber(mediumQualityPosts)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Low Quality Posts</span>
                <span>{formatNumber(lowQualityPosts)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Quality Score</span>
                <span>{avgQualityScore.toFixed(1)}/100</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Content Recommendations</h3>
          </div>
          <div className={styles.recommendationsList}>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Focus on creating high-quality, engaging content</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Experiment with different content types to see what works</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Use consistent posting schedules to maintain quality</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderAudienceTab = () => (
    <>
      {/* Audience Metrics Grid */}
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Audience Quality Score"
          value={audienceQualityScore}
          icon={<FontAwesomeIcon icon="users" />}
          color="blue"
        />
        <MetricCard
          title="Churn Rate"
          value={churnRate}
          format="percentage"
          icon={<FontAwesomeIcon icon="user-minus" />}
          color="red"
        />
        <MetricCard
          title="Retention Rate"
          value={retentionRate}
          format="percentage"
          icon={<FontAwesomeIcon icon="user-plus" />}
          color="green"
        />
        <MetricCard
          title="Activity Rate"
          value={activityRate}
          format="percentage"
          icon={<FontAwesomeIcon icon="heart" />}
          color="purple"
        />
      </div>

      {/* Audience Charts */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Audience Metrics Trends</h3>
          </div>
          <AudienceMetricsTrendsChart data={audienceMetricsTrendsData} />
        </div>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Audience Quality Factors</h3>
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.metricsList}>
              <div className={styles.metricItem}>
                <span>Engagement Rate</span>
                <span>{formatPercentage(audienceMetrics?.qualityMetrics?.qualityFactors?.engagementRate || 0)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Retention Rate</span>
                <span>{formatPercentage(audienceMetrics?.qualityMetrics?.qualityFactors?.retentionRate || 0)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Activity Rate</span>
                <span>{formatPercentage(audienceMetrics?.qualityMetrics?.qualityFactors?.activityRate || 0)}</span>
              </div>
              <div className={styles.metricItem}>
                <span>Loyalty Rate</span>
                <span>{formatPercentage(audienceMetrics?.qualityMetrics?.qualityFactors?.loyaltyRate || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audience Insights */}
      <div className={styles.analyticsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>Audience Recommendations</h3>
          </div>
          <div className={styles.recommendationsList}>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Focus on reducing churn by improving content quality</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Engage with your audience to build stronger relationships</span>
            </div>
            <div className={styles.recommendationItem}>
              <FontAwesomeIcon icon="lightbulb" className={styles.recommendationIcon} />
              <span>Create content that encourages audience participation</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'engagement':
        return renderEngagementTab();
      case 'growth':
        return renderGrowthTab();
      case 'content':
        return renderContentTab();
      case 'audience':
        return renderAudienceTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className={styles.dashboard}>
      {/* Tab Navigation */}
      <div className={styles.tabNavigation}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Dashboard;
